import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Briefcase, CheckCircle2, Compass, FileText, FolderKanban, Hammer, LineChart, Users, Wrench } from 'lucide-react';

export const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/d947f440-07f8-41e8-916c-6e89d8965b4e/images/09089e2a393da831e626cdaeaafa3d7f8e343e3fca28e42ea774f00c0d665816.jpeg';
const LEARN_IMG = 'https://images.pexels.com/photos/34803994/pexels-photo-34803994.jpeg?auto=compress&cs=tinysrgb&w=1200';

export const Eyebrow = ({ children, light }) => (
  <p className={`text-[11px] font-semibold tracking-[0.25em] uppercase mb-3 ${light ? 'text-teal-600' : 'text-teal-300'}`}>{children}</p>
);

export const TealButton = ({ to, children, testid }) => (
  <Link to={to} data-testid={testid}
    className="group inline-flex items-center gap-2 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold px-7 py-3.5 rounded-full transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
    {children} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
  </Link>
);

const PILLARS = [
  [Compass, 'Career clarity', 'Explore roles, understand what fits you and make confident decisions.'],
  [BarChart3, 'Practical skills', 'Build in-demand skills through real-world projects and structured learning paths.'],
  [FileText, 'Stronger profile', 'Improve your resume, portfolio and communication skills.'],
  [Briefcase, 'Opportunities', 'Access internships, projects and job opportunities from our network.'],
];

export const Hero = () => (
  <section className="relative bg-[#0b1f4d] text-white overflow-hidden" data-testid="home-hero">
    <img src={HERO_IMG} alt="Student looking ahead with confidence" className="absolute inset-y-0 right-0 h-full w-full lg:w-[62%] object-cover object-center opacity-40 lg:opacity-100" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0b1f4d] via-[#0b1f4d]/90 lg:via-[#0b1f4d]/70 to-transparent" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-36 lg:pt-44 pb-16">
      <div className="max-w-xl animate-[fadeUp_0.7s_ease-out_both]">
        <Eyebrow>Your career journey</Eyebrow>
        <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
          From where you are to where <span className="text-teal-300">you want to be.</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">Career clarity, practical skills and real opportunities, all in one place for India's students and early-career professionals.</p>
        <div className="mt-8"><TealButton to="/network" testid="hero-start-journey-btn">Start My Career Journey</TealButton></div>
        <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
          {['Free to join', 'Personalized guidance', 'Practical, real-world skills'].map((t) => <li key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-teal-300" />{t}</li>)}
        </ul>
      </div>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PILLARS.map(([Icon, t, d], i) => (
          <div key={t} style={{ animationDelay: `${0.15 + i * 0.08}s` }} data-testid={`home-pillar-${i}`}
            className="animate-[fadeUp_0.6s_ease-out_both] rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-6 hover:border-teal-300/40 hover:bg-white/[0.07] transition-colors">
            <span className="w-11 h-11 rounded-xl bg-teal-400/10 text-teal-300 flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></span>
            <p className="font-outfit font-semibold text-lg">{t}</p>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const LearnByDoing = () => (
  <section className="bg-[#0b1f4d] text-white" data-testid="home-learn">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Eyebrow>Learn by doing</Eyebrow>
        <h2 className="font-outfit text-3xl sm:text-4xl font-bold leading-tight">Build skills that take you further.</h2>
        <p className="mt-5 text-slate-300 leading-relaxed max-w-lg">Work on role-based projects inspired by real workplace problems. Get expert feedback and build a portfolio that shows what you can do.</p>
        <div className="mt-8"><TealButton to="/learning-paths" testid="home-explore-learning-btn">Explore Learning Paths</TealButton></div>
      </div>
      <div className="relative rounded-2xl overflow-hidden border border-white/10 min-h-[300px]">
        <img src={LEARN_IMG} alt="Laptop with code" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1f4d] via-[#0b1f4d]/80 to-transparent" />
        <ul className="relative p-8 space-y-6">
          {[[Hammer, 'Real-world projects'], [Wrench, 'Expert feedback'], [FolderKanban, 'Build your portfolio'], [LineChart, 'Showcase your progress']].map(([Icon, t]) => (
            <li key={t} className="flex items-center gap-3 text-sm font-medium"><Icon className="w-4 h-4 text-teal-300" />{t}</li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export const Opportunities = () => (
  <section className="bg-slate-50" data-testid="home-opportunities">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-[1fr_1.4fr] gap-10 items-center">
      <div>
        <Eyebrow light>Real opportunities</Eyebrow>
        <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#0b1f4d] leading-tight">Get closer to the right opportunities.</h2>
        <p className="mt-5 text-slate-600 leading-relaxed">Access internships, projects and job opportunities through our partner network, and get guidance to help you succeed.</p>
        <div className="mt-8"><TealButton to="/network" testid="home-join-network-btn">Join the Talent Network</TealButton></div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {[[Briefcase, 'Internships', 'Gain real experience and build your skills.'], [BarChart3, 'Early-career roles', 'Explore full-time opportunities.'], [Users, 'Industry exposure', 'Connect with employers and learn from professionals.']].map(([Icon, t, d]) => (
          <div key={t} className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgba(11,31,77,0.06)] hover:-translate-y-1 transition-transform">
            <Icon className="w-6 h-6 text-teal-600 mb-5" />
            <p className="font-outfit font-semibold text-[#0b1f4d]">{t}</p>
            <p className="text-sm text-slate-500 mt-2">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

